import {
    Button, ChangeEvent, ClickEvent, DataSourceRemoteDataChangeEvent, Layout, ModelDataChangeType, Table,
    TableSelectionEvent, Toast
} from "@mcleod/components";
import { Api, Logger, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutOfficePayrollToolPanel } from "./autogen/AutogenLayoutOfficePayrollToolPanel";
import { CommonDialogs } from "@mcleod/common";
import { DriverSettlementHold } from "@mcleod/settlement/src/driver-settlement/DriverSettlementHold";
import { DriverSettlementPendingReview } from "@mcleod/settlement/src/driver-settlement/DriverSettlementPendingReview";
import { OrderPayablesProcess } from "@mcleod/settlement/src/OrderPayablesProcess";
import { BulkUpdatesConfirmation } from "./BulkUpdatesConfirmation";
import { OfficePayrollProcessingPayeeDetail } from "./OfficePayrollProcessingPayeeDetail";

const log: Logger = LogManager.getLogger("OfficePayrollToolPanel");

export class OfficePayrollToolPanel extends AutogenLayoutOfficePayrollToolPanel {
    private table: Table;

    override onLoad(): void {
        this.addMountListener(() => {
            this.initialize(this.owner);
        });
    }

    initialize(layout: OfficePayrollProcessingPayeeDetail) {
        this.table = layout.tablePayAdjustments;
        if (this.getAllVisibleButtons()?.length > 0) {
            this.table?.addSelectionListener((event: TableSelectionEvent) => {
                this.tableOnSelect(event);
            });
            this.table?.dataSource?.addRemoteDataChangeListener((event: DataSourceRemoteDataChangeEvent) => this.onRemoteDataChange(event));
        } else {
            this.removeAll();
        }
    }

    onRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        if (event.change.type === ModelDataChangeType.DELETE) {
            const recordId = event.change.data.get("record_id");
            if (recordId != null && this.table.selectedRow != null) {
                const selectedMoveId = this.table.selectedRow.data.get("record_id");
                if (recordId === selectedMoveId)
                    this.table.selectedRow = null;
            }
        }
    }

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    private tableOnSelect(event: TableSelectionEvent) {
        const modelRow = event.newRow?.data as ModelRow;
        this.syncButtons(modelRow ?? null);
    }

    private syncButtons(modelRow: ModelRow | null) {
        if (!this.panelButtons) return;
        const buttons = this.panelButtons.findComponentsByType(Button) || [];
        buttons.forEach(btn => btn.enabled = modelRow != null);
    }

    buttonUpdateStatusesOnClick(event: ClickEvent) {
        const selectedRows = this.table.selectedRows;
        const layout = Layout.getLayout("lme/backoffice/officepayroll/BulkUpdatesConfirmation") as BulkUpdatesConfirmation;
        const recordCount = selectedRows.length;
        const confirmDialog = CommonDialogs.createDialog(layout, {
            title: `Update Status (${recordCount} ${recordCount > 1 ? "records" : "record"})`,
            okVisible: true
        });
        layout.addLayoutLoadListener(() => {
            if ((this.table.parent instanceof DriverSettlementHold || this.table.parent instanceof DriverSettlementPendingReview) && !this.table.selectedRows[0].data.isNull("payee_process_status")) {
                layout.textboxStatus.removeItemsWithValue("Y");
                layout.textboxStatus.setSelectedItemFromValue("N");
            } else {
                if (this.table.selectedRows.every(row => row.data.get("ready_to_pay_flag") === "Y")) {
                    layout.textboxStatus.setSelectedItemFromValue("N");
                } else {
                    layout.textboxStatus.setSelectedItemFromValue("Y");
                }
            }
            layout.textboxStatus.addAfterDropdownSelectionListener(() => {
                layout.checkboxConfirmVoid.enabled = layout.textboxStatus.valueAsString == "V";
                if (layout.checkboxConfirmVoid.enabled) {
                    confirmDialog.setOkButtonProps({ enabled: false });
                } else {
                    confirmDialog.setOkButtonProps({ enabled: true });
                }

            });
            layout.checkboxConfirmVoid.addChangeListener((event: ChangeEvent) => {
                confirmDialog.setOkButtonProps({ enabled: event.newValue });
            });
        });

        confirmDialog.show().then(() => Promise.resolve(confirmDialog).then(async result => {
            if (!result.wasCancelled) {
                Api.post("lme/backoffice/officepayroll/update-statuses", {
                    status: layout.textboxStatus.selectedItem.value,
                    pay_adjustment_ids: selectedRows.map(row => row.data.get("record_id")).join(",")
                }).then((response) => {
                    const data = response?.data[0];
                    if (data == null) {
                        log.error("No data returned from update-statuses API call.");
                        return;
                    }
                    if (!StringUtil.isEmptyString(data?.error_reasons)) {
                        CommonDialogs.showError(data.error_reasons, `${data.error_count} record${data.error_count > 1 ? "s" : ""} failed to update.`);
                    } else {
                        Toast.showSuccessToast(`Updated ${data.num_records_processed} record${data.num_records_processed > 1 ? "s" : ""} to ${layout.textboxStatus.selectedItem.displayValue} status.`);

                        if (this.table.parent instanceof OrderPayablesProcess) {
                            (this.table.parent as OrderPayablesProcess).resetProcessButtonCaption();
                        }
                    }
                    this.table.selectedRows = null;
                });
            }
        }));
    }
}
